import { StateObservable, combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, startWith } from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import { ReduxAction } from '@repo/utils/types';
import Integration, {
  DeviceIntegrationProvider,
  IntegrationListResponse,
} from '@repo/utils/models/Integration';
import { apiObservable } from '@repo/utils/helpers/api';
import AppState from '../reducers/app-state';
import { selectIntegrationByID } from '../reducers/user/integrations';
import { alertUser } from './alerts';
import { Dispatch } from 'redux';

/* Epic actions */
const integrationActions = requestActions('INTEGRATION', ['fetchAll', 'fetch', 'update', 'delete']);
const externalIntegrationActions = requestActions('EXTERNAL_INTEGRATION', [
  'fetchAll',
  'delete',
  'update',
]);
const integrationSyncActions = requestActions('INTEGRATION_SYNC', ['create']);
const cardDavIntegrationActions = requestActions('CARDDAV_INTEGRATION', [
  'fetch',
  'create',
  'delete',
]);
const cardDavLoginActions = requestActions('CARDDAV_LOGIN', ['create']);

/* Component actions */
export const fetchAllIntegrations = (
  { isFirstLoad, ...props }: { isFirstLoad?: boolean } = { isFirstLoad: false },
) => integrationActions['fetchAll']({ isFirstLoad, ...props });
export const archiveIntegration = (integration: Integration) =>
  integrationActions['delete']({ integration, action: 'archive' });
export const deleteIntegration = (integration: Integration) =>
  integrationActions['delete']({ integration, action: 'delete' });
export const syncIntegration = (integration: Integration) =>
  integrationSyncActions['create']({ integration });

export const updateIntegrationVersion = (provider: DeviceIntegrationProvider, version: number) => ({
  type: 'UPDATE_INTEGRATION',
  payload: { provider, version },
});

export const integrationDeleted = (id: string) => ({
  type: 'INTEGRATION_DELETED',
  payload: { id },
});

export const integrationConnectionSuccessDispatch = (dispatch: Dispatch) => {
  window.desktopIntegrations?.onConnection(() => {
    dispatch(fetchExternalIntegrations());
  });
};

const fetchAllIntegrationsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_ALL_INTEGRATIONS'),
    mergeMap(({ payload }) =>
      apiObservable<IntegrationListResponse[]>('GET', '/integrations/', undefined, {
        apiVersion: 'v2',
      }).pipe(
        map((response) => {
          return integrationActions['fetchAllSucceeded']({
            ...response,
            newlySyncedPlatform: payload?.provider,
            isFirstLoad: payload?.isFirstLoad,
          });
        }),
        startWith(integrationActions['fetchAllRequested']()),
        catchError((err) => of(integrationActions['fetchAllFailed'](err))),
      ),
    ),
  );

// Currently we only fetch an individual integration following a successful sync. Will need to update
// this epic for anything beyond this use case.
const updateStateFollowingSyncEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('INTEGRATION_COMPLETED_SYNC'),
    mergeMap(({ payload: { id, provider } }) => {
      // If this is the first sync (i.e. the integration was just added), we won't have the ID yet -- fetch all integrations instead
      const integrationLoaded = id && selectIntegrationByID(id)(state$.value);

      if (integrationLoaded) {
        return apiObservable<Integration>('GET', `/integrations/${id}/`, undefined, {
          apiVersion: 'v2',
        }).pipe(
          map(integrationActions['fetchSucceeded']),
          startWith(integrationActions['fetchRequested']()),
          catchError((err) => of(integrationActions['fetchFailed'](err))),
        );
      } else {
        return of(integrationActions['fetchAll']({ provider }));
      }
    }),
  );

const updateIntegrationMetadataEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_INTEGRATION'),
    filter(({ payload }) => !!payload.version),
    mergeMap(({ payload: { provider, version } }) =>
      apiObservable<void>(
        'PATCH',
        `/integrations/${provider}/metadata/`,
        { version },
        { apiVersion: 'v2' },
      ).pipe(
        map(integrationActions['updateSucceeded']),
        startWith(
          integrationActions['updateRequested']({ provider, update: { metadata: { version } } }),
        ),
        catchError((err) => of(integrationActions['updateFailed'](err))),
      ),
    ),
  );

const removeIntegrationEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_INTEGRATION'),
    mergeMap(({ payload: { integration, action } }) =>
      apiObservable(
        'DELETE',
        `/integrations/${integration.id}/`,
        {
          action,
        },
        {
          apiVersion: 'v2',
        },
      ).pipe(
        map((resp) => integrationActions['deleteSucceeded']({ ...resp, integration })),
        startWith(integrationActions['deleteRequested'](integration)),
        catchError((err) => of(integrationActions['deleteFailed'](err))),
      ),
    ),
  );

const syncIntegrationEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_INTEGRATION_SYNC'),
    mergeMap(({ payload: { integration } }) =>
      apiObservable('POST', `/integrations/${integration.id}/sync/`, undefined, {
        apiVersion: 'v2',
      }).pipe(
        map(integrationSyncActions['createSucceeded']),
        startWith(integrationSyncActions['createRequested'](integration)),
        catchError((err) => of(integrationSyncActions['createFailed']({ integration, ...err }))),
      ),
    ),
  );

const clearProfileUpdateSuccessEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_CONTACT_PROFILE_SUCCEEDED'),
    delay(1000 * 3),
    map(({ payload }) => ({ type: 'CLEAR_PROFILE_UPDATE_STATUS', payload })),
  );

const clearProfileUpdateErrorEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_CONTACT_PROFILE_FAILED'),
    delay(1000 * 60),
    map(({ payload }) => ({ type: 'CLEAR_PROFILE_UPDATE_STATUS', payload })),
  );

// All other "external" integrations (chatgpt, zapier)
export const fetchExternalIntegrations = externalIntegrationActions['fetchAll'];
export const deleteExternalIntegration = (integration: Integration) =>
  externalIntegrationActions['delete']({ integration });
export const syncExternalIntegration = (integration: Integration) =>
  externalIntegrationActions['update']({ integration });
export const reimportExternalIntegration = (integration: Integration) =>
  externalIntegrationActions['update']({ integration, action: 'reimport' });

const fetchExternalIntegrationsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_ALL_EXTERNAL_INTEGRATIONS'),
    mergeMap(() =>
      apiObservable<IntegrationListResponse[]>('GET', '/integrations/external/', undefined, {
        apiVersion: 'v2',
      }).pipe(
        map(externalIntegrationActions['fetchAllSucceeded']),
        startWith(externalIntegrationActions['fetchAllRequested']()),
        catchError((err) => of(externalIntegrationActions['fetchAllFailed'](err))),
      ),
    ),
  );

const deleteExternalIntegrationEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_EXTERNAL_INTEGRATION'),
    mergeMap(({ payload: { integration } }) =>
      apiObservable('DELETE', `/integrations/external/${integration.id}/`, undefined, {
        apiVersion: 'v2',
      }).pipe(
        map((resp) => externalIntegrationActions['deleteSucceeded']({ ...resp, integration })),
        startWith(externalIntegrationActions['deleteRequested'](integration)),
        catchError((err) => of(externalIntegrationActions['deleteFailed'](err))),
      ),
    ),
  );

const syncExternalIntegrationEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_EXTERNAL_INTEGRATION'),
    mergeMap(({ payload: { integration, action } }) =>
      apiObservable(
        'PATCH',
        `/integrations/external/${integration.id}/${action === 'reimport' ? 'reimport' : 'sync'}/`,
        undefined,
        {
          apiVersion: 'v2',
        },
      ).pipe(
        map(() => externalIntegrationActions['updateSucceeded'](integration)),
        startWith(externalIntegrationActions['updateRequested'](integration)),
        catchError((err) =>
          of(externalIntegrationActions['updateFailed']({ integration, ...err })),
        ),
      ),
    ),
  );

const alertExternalSyncStartedEpic = (action$: Observable<ReduxAction>) => {
  return action$.pipe(
    ofType('UPDATE_EXTERNAL_INTEGRATION_SUCCEEDED'),
    map(({ payload }) => {
      return alertUser(
        `${payload.displayName ? payload.displayName + ' ' : ''}resync started`,
        'success',
      );
    }),
  );
};

// CardDav
export const fetchCardDavIntegration = cardDavIntegrationActions['fetch'];
export const createCardDavIntegration = cardDavIntegrationActions['create'];
export const deleteCardDavIntegration = cardDavIntegrationActions['delete'];
export const createCardDavLogin = cardDavLoginActions['create'];

const fetchCardDavIntegrationEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_CARDDAV_INTEGRATION'),
    mergeMap(() =>
      apiObservable<Integration>('GET', '/integrations/carddav/', undefined, {
        apiVersion: 'v2',
      }).pipe(
        map(cardDavIntegrationActions['fetchSucceeded']),
        startWith(cardDavIntegrationActions['fetchRequested']()),
        catchError((err) => of(cardDavIntegrationActions['fetchFailed'](err))),
      ),
    ),
  );

const createCardDavIntegrationEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_CARDDAV_INTEGRATION'),
    mergeMap(({ payload }) =>
      apiObservable<Integration>('POST', '/integrations/carddav/', payload, {
        apiVersion: 'v2',
      }).pipe(
        map(cardDavIntegrationActions['createSucceeded']),
        startWith(cardDavIntegrationActions['createRequested']()),
        catchError((err) => of(cardDavIntegrationActions['createFailed'](err))),
      ),
    ),
  );

const deleteCardDavIntegrationEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_CARDDAV_INTEGRATION'),
    mergeMap(() =>
      apiObservable('DELETE', '/integrations/carddav/disconnect/', undefined, {
        apiVersion: 'v2',
      }).pipe(
        map(cardDavIntegrationActions['deleteSucceeded']),
        startWith(cardDavIntegrationActions['deleteRequested']()),
        catchError((err) => of(cardDavIntegrationActions['deleteFailed'](err))),
      ),
    ),
  );

const createCardDavLoginEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_CARDDAV_LOGIN'),
    mergeMap(({ payload }) =>
      apiObservable<void>('POST', '/integrations/carddav/login/', payload, {
        apiVersion: 'v2',
      }).pipe(
        map(cardDavLoginActions['createSucceeded']),
        startWith(cardDavLoginActions['createRequested']()),
        catchError((err) => of(cardDavLoginActions['createFailed'](err))),
      ),
    ),
  );

export const integrationsEpic = combineEpics(
  fetchAllIntegrationsEpic,
  updateStateFollowingSyncEpic,
  updateIntegrationMetadataEpic,
  removeIntegrationEpic,
  syncIntegrationEpic,
  clearProfileUpdateSuccessEpic,
  clearProfileUpdateErrorEpic,
  fetchExternalIntegrationsEpic,
  deleteExternalIntegrationEpic,
  syncExternalIntegrationEpic,
  alertExternalSyncStartedEpic,
  fetchCardDavIntegrationEpic,
  createCardDavIntegrationEpic,
  deleteCardDavIntegrationEpic,
  createCardDavLoginEpic,
);
